/** The trading session clock. */

import { zonedParts } from './jalali'

export interface SessionWindow {
  openMinute: number
  closeMinute: number
  timeZone: string
  /** Weekday indexes that are closed, where 0 is شنبه. Empty by default. */
  closedWeekdays: number[]
}

export const SESSION: SessionWindow = {
  openMinute: 9 * 60,
  closeMinute: 17 * 60,
  timeZone: 'Asia/Tehran',
  closedWeekdays: [],
}

export const SESSION_SECONDS_PER_DAY = (SESSION.closeMinute - SESSION.openMinute) * 60

const DAY_MS = 86_400_000

function weekdayIndex(date: Date, window: SessionWindow): number {
  const p = zonedParts(date, window.timeZone)
  const utc = Date.UTC(p.year, p.month - 1, p.day)
  // getUTCDay returns 0 for Sunday. شنبه is Saturday, so shift by one.
  return (new Date(utc).getUTCDay() + 1) % 7
}

function minuteOfDay(date: Date, window: SessionWindow): number {
  const p = zonedParts(date, window.timeZone)
  return p.hour * 60 + p.minute + p.second / 60
}

/** Midnight in the business zone, as an instant, for the day containing `date`. */
function startOfBusinessDay(date: Date, window: SessionWindow): Date {
  const p = zonedParts(date, window.timeZone)
  const approx = Date.UTC(p.year, p.month - 1, p.day, 0, 0, 0)
  // Re-read the zone offset at that wall clock moment.
  const probe = new Date(approx)
  const probeParts = zonedParts(probe, window.timeZone)
  const drift =
    (probeParts.hour * 3600 + probeParts.minute * 60 + probeParts.second) * 1000
  return new Date(approx - drift)
}

/** Is the market open at this instant. */
export function isSessionOpen(at: Date = new Date(), window: SessionWindow = SESSION): boolean {
  if (window.closedWeekdays.includes(weekdayIndex(at, window))) return false
  const m = minuteOfDay(at, window)
  return m >= window.openMinute && m < window.closeMinute
}

/** Which of the three spans of the business day an instant sits in. */
export type SessionBand = 'BEFORE' | 'IN' | 'AFTER'

export function sessionBand(at: Date = new Date(), window: SessionWindow = SESSION): SessionBand {
  const m = minuteOfDay(at, window)
  if (m < window.openMinute) return 'BEFORE'
  if (m < window.closeMinute) return 'IN'
  return 'AFTER'
}

export interface SessionState {
  open: boolean
  /** Instant the current session closes, when open. */
  closesAt: Date | null
  /** Instant the next session opens, when closed. */
  opensAt: Date | null
}

export function sessionState(at: Date = new Date(), window: SessionWindow = SESSION): SessionState {
  if (isSessionOpen(at, window)) {
    const midnight = startOfBusinessDay(at, window)
    return {
      open: true,
      closesAt: new Date(midnight.getTime() + window.closeMinute * 60_000),
      opensAt: null,
    }
  }
  return { open: false, closesAt: null, opensAt: nextOpen(at, window) }
}

/** The next instant the market opens at or after `at`. */
export function nextOpen(at: Date, window: SessionWindow = SESSION): Date {
  let midnight = startOfBusinessDay(at, window)
  for (let i = 0; i < 400; i += 1) {
    const open = new Date(midnight.getTime() + window.openMinute * 60_000)
    const day = weekdayIndex(open, window)
    if (!window.closedWeekdays.includes(day) && open.getTime() > at.getTime()) return open
    midnight = startOfBusinessDay(new Date(midnight.getTime() + DAY_MS + 3 * 3600_000), window)
  }
  throw new Error('no trading day found within 400 days')
}

/** Session seconds elapsed between two instants. Never negative. */
export function sessionSecondsBetween(
  from: Date,
  to: Date,
  window: SessionWindow = SESSION,
): number {
  if (to.getTime() <= from.getTime()) return 0
  let total = 0
  let cursor = startOfBusinessDay(from, window)
  const guard = Math.ceil((to.getTime() - from.getTime()) / DAY_MS) + 3

  for (let i = 0; i < guard; i += 1) {
    const open = new Date(cursor.getTime() + window.openMinute * 60_000)
    const close = new Date(cursor.getTime() + window.closeMinute * 60_000)
    if (!window.closedWeekdays.includes(weekdayIndex(open, window))) {
      const start = Math.max(open.getTime(), from.getTime())
      const end = Math.min(close.getTime(), to.getTime())
      if (end > start) total += (end - start) / 1000
    }
    if (close.getTime() >= to.getTime()) break
    cursor = startOfBusinessDay(new Date(cursor.getTime() + DAY_MS + 3 * 3600_000), window)
  }
  return Math.floor(total)
}

export function addSessionSeconds(
  from: Date,
  sessionSeconds: number,
  window: SessionWindow = SESSION,
): Date {
  let remaining = Math.max(0, Math.floor(sessionSeconds))
  if (remaining === 0) return new Date(from.getTime())

  let cursor = from
  for (let i = 0; i < 400; i += 1) {
    const midnight = startOfBusinessDay(cursor, window)
    const open = new Date(midnight.getTime() + window.openMinute * 60_000)
    const close = new Date(midnight.getTime() + window.closeMinute * 60_000)
    const closedDay = window.closedWeekdays.includes(weekdayIndex(open, window))

    if (!closedDay && cursor.getTime() < close.getTime()) {
      const start = Math.max(cursor.getTime(), open.getTime())
      const available = (close.getTime() - start) / 1000
      if (available >= remaining) return new Date(start + remaining * 1000)
      remaining -= Math.floor(available)
    }
    cursor = nextOpen(new Date(midnight.getTime() + DAY_MS - 3600_000), window)
  }
  throw new Error('validity exceeds 400 trading days')
}

/** Session seconds left on something that expires at `expiresAt`. */
export function sessionSecondsRemaining(
  expiresAt: Date,
  at: Date = new Date(),
  window: SessionWindow = SESSION,
): number {
  return sessionSecondsBetween(at, expiresAt, window)
}

export type ValidityPhase = 'running' | 'frozen' | 'urgent' | 'expired'

/** Which of the four validity clock states to draw. Urgent is the last hour. */
export function validityPhase(
  expiresAt: Date,
  at: Date = new Date(),
  window: SessionWindow = SESSION,
  urgentSeconds = 3600,
): ValidityPhase {
  const remaining = sessionSecondsRemaining(expiresAt, at, window)
  if (remaining <= 0) return 'expired'
  if (!isSessionOpen(at, window)) return 'frozen'
  return remaining <= urgentSeconds ? 'urgent' : 'running'
}

export const PRE_EXPIRY_NUDGE_SECONDS = 3600
